import { useCallback, useEffect, useRef, useState } from "react";
import {
  ColumnsDirective,
  ColumnDirective,
  Page,
  Toolbar,
  Edit,
  Inject,
  Filter,
  VirtualScroll,
  Sort,
  ColumnChooser,
  ExcelExport,
  Resize,
} from "@syncfusion/ej2-react-grids";
import { useDispatch } from "react-redux";
import { convertDate } from "../../services/luxonHelper";
import "./issues.css";
import css from "./index.module.css";
import IssueDetails from "./IssueDetails/IssueDetails";
import nodata from "../../assets/icons/svg/InspectionFormNoData.svg";
import ToolTipComponent from "../../cubeComponent/html/tooltip";
import GridComponent from "../../cubeComponent/html/grid";
import { getUrlForImage } from "../../cubeComponent/constants/globalFunctions";
import { getCompleteIssuesDataByPortal, handleIssuesExportAsPDF } from "../../actions/bcfData";
import { useLocation, useParams } from "react-router-dom";
import { issuesService } from "../../services/dataManager";
import { Query } from "@syncfusion/ej2-data";
import { getStatusBtnStyle } from "../Reviews/ReviewsHelper";
import CreateIssueModal from "./CreateIssueModal";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import CreateIcon from "@Components/StickySidebarViewer/assets/CreateIcon";
import { setShowStickySidebar, setSidebarSelectedCell } from "../../actions/stickySidebar";
import IconComponent from "@CubeComponents/icons";
import { showToastNotification } from "../../actions/toast";
import { TOAST_TYPE_ERROR } from "../../services/constants";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { getImageUrl } from "@Utils";

function Issues({
  getAllIssuesInSpace,
  allowOpenSidebar = true,
  issueSelectionCallback,
  showTitleLine = true,
  allowTools = true,
  allowStickySidebar = true,
  fileVersionIds = [],
  showCheckboxes = false,
  disabledIssueIds = [],
  showStatusCol = true,
  getStandaloneIssuesForSpaceId,
  spaceId,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [openIssuesDetails, setOpenIssuesDetails] = useState(false);
  const [sidebardata, setSidebardata] = useState([]);
  const [showIllustration, setShowIllustration] = useState(false);
  const [openCreateIssue, setOpenCreateIssue] = useState(false);

  const filterSettings = { type: "Menu" };
  const dateFilter = { type: "Menu" };
  const editSettings = {
    allowDeleting: true,
    mode: "Dialog",
    showDeleteConfirmDialog: true,
  };
  const pageSettings = { pageSizes: true, pageSize: 10 };
  const dataGrid = useRef();
  const location = useLocation();
  const params = useParams();
  const urlParams = new URLSearchParams(location.search);

  const query = new Query().addParams("projectId", params.projectId).take(10);

  const severityTypes = {
    MINOR: t("Minor"),
    MAJOR: t("Major"),
  };

  if (fileVersionIds?.length) {
    query.addParams("fileId", fileVersionIds);
  }

  if (getStandaloneIssuesForSpaceId) {
    query.addParams("getStandaloneIssuesForSpaceId", getStandaloneIssuesForSpaceId);
  }
  if (getAllIssuesInSpace) {
    query.addParams("spaceId", spaceId);
  }

  useEffect(() => {
    if (dataGrid.current) {
      dataGrid.current.refresh();
    }

    if (allowStickySidebar) {
      dispatch(setSidebarSelectedCell("Issues"));
      dispatch(setShowStickySidebar(true));
    } else {
      dispatch(setSidebarSelectedCell(""));
      dispatch(setShowStickySidebar(false));
    }
  }, []);

  const createBtnTemplate = () => (
    <ButtonComponent id="custom_createMail" buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} className="mx-4">
      <span className="flex items-center">
        <CreateIcon size="15" className="me-2" />
        {t("Create")}
      </span>
    </ButtonComponent>
  );

  const createOption = {
    tooltipText: t("Create"),
    id: "custom_createIssue",
    template: createBtnTemplate,
  };

  const getIssuesToolbarOptions = () => {
    let options;
    if (allowTools) {
      options = [
        ...(!urlParams.get("byPortal") && params.projectId ? [createOption] : []),
        "ColumnChooser",
        "ExcelExport",
        ...(!urlParams.get("byPortal") && params.projectId ? ["PdfExport"] : []),
      ];
    } else {
      options = ["ColumnChooser"];
    }
    return options;
  };
  const toolbarOptions = getIssuesToolbarOptions();

  function openIssueSidebar(args) {
    if (Array.isArray(args.data)) {
      args.data = args.data[0];
    }
    const rowdata = args.data;
    setSidebardata(rowdata);
    setOpenIssuesDetails(true);
  }

  function selectingEvents(args) {
    if (dataGrid?.current) {
      if (dataGrid.current.getSelectedRows().length == 1 && allowOpenSidebar) {
        openIssueSidebar(args);
      } else {
        closeIssueSidebar();
      }

      if (disabledIssueIds.includes(args.data.id)) {
        args.cancel = true;
        return;
      }

      if (issueSelectionCallback) {
        const selectedRecords = dataGrid.current.getSelectedRecords();
        issueSelectionCallback(selectedRecords);
      }
    }
  }

  const rowDataBound = (args) => {
    if (disabledIssueIds.includes(_.get(args, "data.id"))) {
      args.isSelectable = false;
      args.row.classList.add("e-disabled");
      args.row.style.pointerEvents = "none";
    }
  };

  const headerCellInfo = (args) => {
    if (disabledIssueIds.length && args.node?.children?.length) {
      const firstChild = args.node.children[0];

      if (Array.from(firstChild.classList).includes("e-headerchkcelldiv")) {
        firstChild.style.pointerEvents = "none";
        firstChild.classList.add("e-disabled");
      }
    }
  };

  function closeIssueSidebar() {
    setOpenIssuesDetails(false);
  }

  function showIllustrationHandler() {
    if (dataGrid && dataGrid.current) {
      if (!dataGrid.current.filterModule.currentFilterObject) {
        const records = dataGrid.current.getCurrentViewRecords();
        const shouldShowIllustration = !records?.length;

        setShowIllustration(shouldShowIllustration);
      }
    }
  }

  function dataBound() {
    showIllustrationHandler();
  }

  function excelQueryCellInfo(args) {
    if (args.column.headerText === "Image" && args.data.snapshot) {
      args.image = {
        height: 75,
        base64: args.data.snapshot.replace("data:image/png;base64,", ""),
        width: 75,
      };
      args.value = "";
    } else if (args.column.headerText === "Created On") {
      args.value = convertDate(Number(args.data.createdDate), "dateForProfile");
    } else if (args.column.headerText === "Due Date" && args.data.dueDate) {
      args.value = convertDate(Number(args.data.dueDate), "dateForProfile");
    }
  }

  const handleExportToPdf = async () => {
    const data = {};
    if (!urlParams.get("byPortal") && params.projectId) {
      data.projectId = params.projectId;
    }

    const res = await handleIssuesExportAsPDF(data);
    if (_.get(res, "status") === false) {
      dispatch(
        showToastNotification({
          content: res.errorMessage,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  async function toolbarClickHandler(args) {
    if (dataGrid.current && args.item.id.includes("excelexport")) {
      dataGrid.current.toolbarModule.enableItems([`${dataGrid.current.element.id}_excelexport`], false);
      dataGrid.current.showSpinner();
      const data = {
        value: {
          requiresCounts: true,
          take: dataGrid.current.pageSettings.properties.totalRecordsCount,
        },
        getAllProperties: true,
      };
      if (!urlParams.get("byPortal")) {
        data.value.projectId = params.projectId;
      }

      const allIssuesData = await getCompleteIssuesDataByPortal(data);
      const exportProperties = {
        dataSource: allIssuesData,
      };
      dataGrid.current.excelExport(exportProperties);
    } else if (args.item.properties.id === "custom_createIssue") {
      setOpenCreateIssue(true);
    } else if (args.item.id.includes("pdfexport")) {
      handleExportToPdf();
    }
  }

  const handleNewIssueClick = () => {
    setOpenCreateIssue(true);
  };

  function excelExportComplete() {
    if (dataGrid.current) {
      dataGrid.current.toolbarModule.enableItems([`${dataGrid.current.element.id}_excelexport`], true);
      dataGrid.current.hideSpinner();
    }
  }

  function formatDate(props) {
    const dateFormat = convertDate(Number(props.createdDate), "dateForTooltip");
    return (
      <div style={{ width: "fit-content" }}>
        <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
          <span>{convertDate(Number(props.createdDate), "dateForText")}</span>
        </ToolTipComponent>
      </div>
    );
  }

  function formatDueDate(props) {
    if (props.dueDate) {
      const dateFormat = convertDate(Number(props.dueDate), "dateForTooltip");
      return (
        <div style={{ width: "fit-content" }}>
          <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
            <span>{convertDate(Number(props.dueDate), "dateForText")}</span>
          </ToolTipComponent>
        </div>
      );
    }
    return "-";
  }

  function formatSeverity(props) {
    if (props.severity) {
      return (
        <div style={{ width: "fit-content" }}>
          <ToolTipComponent showTipPointer position="bottom" content={props.severity}>
            <span>{severityTypes[props.severity]}</span>
          </ToolTipComponent>
        </div>
      );
    }
    return "-";
  }

  function issueCounterTemplate(args) {
    return (
      <div className="flex flex-row gap-4 items-center">
        <span>{args.issueCounter}</span>
        {!args.isFormTemplateValid && (
          <IconComponent
            name="ErrorIcon"
            className="h-4 w-4"
            toolTipContent={t("Mandatory custom fields not filled.")}
            position="bottom"
          />
        )}
      </div>
    );
  }

  function statusTemplate(args) {
    const { statusInReview } = args;

    if (!statusInReview?._id) {
      return "-";
    }

    const style = getStatusBtnStyle(statusInReview.category);

    return (
      <div className={css.issueStatusInReview} style={style}>
        <p>{statusInReview.name}</p>
      </div>
    );
  }

  function descriptionTemplate(props) {
    if (props.description?.length > 0) {
      const html = props.description;
      const div = document.createElement("div");
      div.innerHTML = html;
      const text = div.textContent || div.innerText || "";
      return (
        <ToolTipComponent showTipPointer position="bottom" content={text} maxHeight="75px">
          {text}
        </ToolTipComponent>
      );
    }

    return "-";
  }

  const assignTo = (data) => data.assignedToUserName || "-";

  const getUrl = (data) => {
    let url = `${getImageUrl()}/DefaultProjectImage.svg`;
    if (data.snapshot) {
      url = getUrlForImage({ id: data.id }, "IssueImage");
    }

    return url;
  };

  const snapshotTemplate = (data) => {
    const imageUrl = getUrl(data);

    return (
      <div className={css.snapshotStyle}>
        <img src={imageUrl} className={css.snapshotImageStyle} />
      </div>
    );
  };

  const fileTemplate = (args) => {
    const { file } = args;

    if (file) {
      return file;
    }

    return "-";
  };

  const fileVersionTemplate = (args) => {
    const { fileVersionNumber } = args;

    return fileVersionNumber ? `V${fileVersionNumber}` : "-";
  };

  const refreshDataGrid = () => {
    if (dataGrid.current) {
      dataGrid.current.refresh();
    }
  };

  function NoIssues() {
    return (
      <div className="flex justify-center items-center h-full w-full">
        <div className={css.noIssuesDataContainer}>
          <img src={nodata} alt={t("No Data")} />
          <p className={css.noIssuesTitle}>{t("No issues has been created yet.")}</p>
          {allowTools && (
            <ButtonComponent className="mt-4" onClick={handleNewIssueClick} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
              <span className="flex items-center gap-1">
                <IconComponent name="add" />
                <span className="text-primary_text">{t("New")}</span>
              </span>
            </ButtonComponent>
          )}
        </div>
      </div>
    );
  }

  const IssuesData = useCallback(
    () => (
      <div className="flex flex-col h-full w-full overflow-auto">
        {showTitleLine && (
          <div className="flex flex-col py-2">
            <div className="text-2xl font-semibold">{t("Issues")}</div>
            <div className="text-sm">{t("All the issues in this project")}</div>
          </div>
        )}
        <div className={`${css.issuesControlPane} issuesControlPane`}>
          <GridComponent
            toolbar={toolbarOptions}
            editSettings={editSettings}
            dataSource={issuesService}
            ref={dataGrid}
            allowSorting
            allowExcelExport={allowTools}
            excelQueryCellInfo={excelQueryCellInfo}
            filterSettings={filterSettings}
            allowFiltering
            allowPaging
            rowHeight={68}
            height="100%"
            toolbarClick={toolbarClickHandler}
            className={css.tableWrapper}
            rowSelected={selectingEvents}
            rowDeselected={selectingEvents}
            showColumnChooser
            rowDataBound={rowDataBound}
            headerCellInfo={headerCellInfo}
            allowResizing
            pageSettings={pageSettings}
            excelExportComplete={excelExportComplete}
            dataBound={dataBound}
            query={query}
          >
            <ColumnsDirective>
              {showCheckboxes && <ColumnDirective type="checkbox" width="50" />}
              <ColumnDirective
                field="id"
                isPrimaryKey
                showInColumnChooser={false}
                visible={false}
                allowFiltering={false}
                allowSorting={false}
                width="145"
              />
              <ColumnDirective
                field="snapshot"
                headerText={t("Image")}
                clipMode="EllipsisWithTooltip"
                template={snapshotTemplate}
                allowFiltering={false}
                allowSorting={false}
                width="145"
              />
              <ColumnDirective
                field="issueCounter"
                headerText={t("Issue ID")}
                clipMode="EllipsisWithTooltip"
                filter={filterSettings}
                template={issueCounterTemplate}
                width="300"
              />
              <ColumnDirective field="title" headerText={t("Title")} clipMode="EllipsisWithTooltip" />
              <ColumnDirective
                field="description"
                headerText={t("Description")}
                template={descriptionTemplate}
                allowFiltering={false}
                allowSorting={false}
              />
              {showStatusCol && (
                <ColumnDirective
                  field="statusInReview.name"
                  headerText={t("Status")}
                  filter={filterSettings}
                  template={statusTemplate}
                  type="string"
                />
              )}
              {urlParams?.get("byPortal") ? <ColumnDirective field="projectId" headerText={t("Project ID")} /> : null}
              {urlParams?.get("byPortal") ? (
                <ColumnDirective field="projectName" headerText={t("Project Name")} width="155" textAlign="Left" />
              ) : null}

              <ColumnDirective
                field="spaceName"
                headerText={t("Space Name")}
                clipMode="EllipsisWithTooltip"
                filter={filterSettings}
              />
              <ColumnDirective
                field="file"
                headerText={t("File")}
                clipMode="EllipsisWithTooltip"
                type="string"
                filter={filterSettings}
                template={fileTemplate}
              />
              <ColumnDirective
                field="fileVersionNumber"
                headerText={t("File Version")}
                clipMode="EllipsisWithTooltip"
                filter={filterSettings}
                type="string"
                allowFiltering={false}
                template={fileVersionTemplate}
              />
              <ColumnDirective
                field="assignedToUserName"
                headerText={t("Assigned to")}
                filter={filterSettings}
                template={assignTo}
                type="string"
              />
              <ColumnDirective field="createdByName" headerText={t("Created by")} filter={filterSettings} />
              <ColumnDirective
                field="createdDate"
                headerText={t("Created On")}
                type="date"
                template={formatDate}
                filter={dateFilter}
              />
              <ColumnDirective
                field="dueDate"
                headerText={t("Due Date")}
                type="date"
                template={formatDueDate}
                filter={dateFilter}
                allowFiltering={false}
              />
              <ColumnDirective
                field="severity"
                headerText={t("Severity")}
                template={formatSeverity}
                clipMode="EllipsisWithTooltip"
              />
            </ColumnsDirective>
            <Inject services={[Page, Toolbar, Edit, Filter, Sort, VirtualScroll, ColumnChooser, Resize, ExcelExport]} />
          </GridComponent>
        </div>
      </div>
    ),
    [showIllustration],
  );

  return (
    <div className={css.mainWrapper}>
      {showIllustration && NoIssues()}
      {!showIllustration && IssuesData()}
      {openIssuesDetails && <IssueDetails closeIssueSidebar={closeIssueSidebar} rowData={sidebardata} />}
      {openCreateIssue && (
        <CreateIssueModal
          closeModal={() => setOpenCreateIssue(false)}
          refreshDataGrid={refreshDataGrid}
          projectId={params.projectId}
        />
      )}
    </div>
  );
}

export default Issues;
